<template>
	<div class="flex h-full w-full flex-col gap-2 overflow-y-auto sm:pr-4">
		<div class="hidden flex-col gap-2 sm:flex">
			<div
				class="group flex h-14 w-full max-w-[120px] cursor-grab items-center justify-center rounded-sm bg-yellow-300 text-center text-black shadow-sm hover:bg-yellow-400 active:cursor-grabbing"
				data-type="note"
				data-label="Note"
				data-default-config="{}"
				title="Drag to add new"
			>
				<div class="flex flex-col items-center justify-center gap-1">
					<div class="flex min-h-4 items-center justify-center">
						<span class="fa-light fa-notes inline-block"></span>
					</div>
					<span class="text-xs">Add Note</span>
				</div>
			</div>
		</div>

		<div v-for="(group, groupName) in groupedData" :key="groupName" class="hidden sm:block">
			<div
				class="whitespace-nowrapborder-b-2 flex w-full items-center justify-between py-2 text-base font-semibold text-offmaindark dark:text-white"
			>
				{{ groupName }}
			</div>
			<div class="mt-2 grid min-w-max grid-cols-1 gap-2 lg:grid-cols-2 2xl:grid-cols-3">
				<div
					v-for="entry in group.items"
					:key="entry.type"
					:class="[
						'group flex h-14 w-[120px] items-center justify-center overflow-hidden rounded-sm bg-opacity-100 text-center shadow-sm hover:bg-opacity-70',
						entry.disabled
							? 'cursor-not-allowed opacity-30 grayscale'
							: 'cursor-grab active:cursor-grabbing',
						{ [`bg-${entry?.style?.bg}`]: !Array.isArray(entry?.style?.bg) },
						entry.disabled
							? ''
							: Array.isArray(entry?.style?.bg)
								? 'hover:bg-opacity-70'
								: 'hover:bg-' + entry.style.bg + '/70',
						entry.style.text
					]"
					:style="`${Array.isArray(entry?.style?.bg) ? `background-color: rgba(${entry?.style?.bg.join(',')}, var(--tw-bg-opacity));` : ''}`"
					:data-type="entry.disabled ? undefined : entry.type"
					:data-label="entry.name"
					:data-default-config="JSON.stringify(entry.defaultConfig || {})"
					:title="entry.disabled ? 'Not available' : 'Drag to add new'"
					:aria-disabled="entry.disabled"
				>
					<div class="flex flex-col items-center justify-center gap-1">
						<div class="flex min-h-4 items-center justify-center">
							<span class="fa-light" :class="entry.style.icon"></span>
						</div>
						<span class="text-xs">{{ entry.name }}</span>
					</div>
					<div v-if="entry.disabled" class="opacity-30 group-hover:opacity-50">
						<div class="absolute inset-0 flex rotate-[22.5deg] scale-[140%] items-center justify-center">
							<span class="h-1 w-full bg-white"></span>
						</div>
						<div class="absolute inset-0 flex -rotate-[22.5deg] scale-[140%] items-center justify-center">
							<span class="h-1 w-full bg-white"></span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="relative max-w-[calc(100vw-2rem)]">
			<div class="no-scrollbar snap-x snap-mandatory overflow-x-auto pb-4">
				<div class="flex flex-col flex-nowrap sm:hidden">
					<div class="h-12 w-full">
						<div
							class="pointer-events-none absolute left-[calc(50vw-18px)] top-4 flex -translate-x-1/2 gap-2"
						>
							<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 14 14">
								<path
									fill="none"
									stroke="currentColor"
									stroke-linecap="round"
									stroke-linejoin="round"
									d="m13.478 11.832l-.46-2.757a2.573 2.573 0 0 0-2.961-2.114l-2.171.362l-.683-4.09a1.194 1.194 0 0 0-1.374-.98v0c-.65.108-1.09.723-.98 1.374l.894 5.36l-.363.133a1.715 1.715 0 0 0-.643 2.803l.184.19l.954.988M1.75.5L.5 1.75L1.75 3M.5 1.75h3M10.25.5l1.25 1.25L10.25 3m1.25-1.25h-3"
								/>
							</svg>
							<p>Touch here to scroll</p>
						</div>
					</div>
					<div class="flex snap-x snap-mandatory gap-2 pb-4 pt-2">
						<div
							class="group flex h-10 cursor-grab snap-center items-center justify-center whitespace-nowrap rounded-md bg-yellow-300 px-2 py-1 text-center text-black shadow-sm active:cursor-grabbing"
							data-type="note"
							data-label="Note"
							data-default-config="{}"
							title="Drag to add new"
						>
							<div class="flex items-center gap-2">
								<div class="flex min-h-4 items-center justify-center">
									<span class="fa-light fa-notes inline-block"></span>
								</div>
								<span>Add Note</span>
							</div>
						</div>
						<div
							v-for="item in usableFlowNodes"
							:key="item.type"
							:class="[
								'group flex h-10 snap-center items-center justify-center whitespace-nowrap rounded-md bg-opacity-100 px-2 py-1 text-center shadow-sm',
								item.disabled ? 'hidden' : 'cursor-grab active:cursor-grabbing',
								{ [`bg-${item?.style?.bg}`]: !Array.isArray(item?.style?.bg) },
								item.disabled ? 'brightness-75 saturate-0' : 'hover:bg-opacity-70',
								item.style.text
							]"
							:data-type="item.disabled ? undefined : item.type"
							:data-label="item.name"
							:style="`${Array.isArray(item?.style?.bg) ? `background-color: rgba(${item?.style?.bg.join(',')}, var(--tw-bg-opacity));` : ''}`"
							:data-default-config="JSON.stringify(item.defaultConfig || {})"
							title="Drag to add new"
							:disabled="item.disabled"
						>
							<div class="flex items-center gap-2">
								<div v-if="item.style.icon" class="pr-1">
									<span class="fa-light" :class="item.style.icon"></span>
								</div>
								<span>{{ item.name }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue'
import { SurfaceDrop } from '@jsplumbtoolkit/browser-ui-vue3'

export default defineComponent({
	mixins: [SurfaceDrop],
	props: ['flowNodes'],
	data: function () {
		return {
			groupedData: {},
			defaultValue: 'Routing'
		}
	},
	computed: {
		usableFlowNodes() {
			return this.flowNodes.filter((node) => node.category)
		}
	},
	mounted() {
		let visible = true

		this.flowNodes.forEach((node) => {
			if (!node.category) return

			if (!this.groupedData[node.category]) {
				this.groupedData[node.category] = {
					visible,
					items: [node]
				}
				visible = false
			} else this.groupedData[node.category].items.push(node)
		})

		for (let group in this.groupedData) {
			this.groupedData[group].items.sort((a, b) => {
				if (a.disabled && !b.disabled) return 1
				if (!a.disabled && b.disabled) return -1
				return a.name.localeCompare(b.name)
			})
		}
	}
})
</script>

<style scoped>
@media (max-width: 640px) {
	.no-scrollbar {
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */
	}

	.no-scrollbar::-webkit-scrollbar {
		display: none; /* Chrome, Safari, Opera */
	}

	.snap-x {
		-webkit-overflow-scrolling: touch;
	}
}
</style>
